import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { ruleListTextStrings } from '../../util';
import RuleListItem from '../RuleListItem';

import styles from './PasswordRulesValidationList.module.scss';

export default function PasswordRulesValidationList({
  displayInline,
  passwordPolicyRules,
  passwordPolicyRulesLoading,
  passwordValidationStatus,
  passwordValidationLoading,
}) {
  if (passwordPolicyRulesLoading) {
    return (
      <div
        className={classnames({
          [styles.inlineWrapper]: displayInline,
          [styles.wrapper]: !displayInline,
        })}
        data-testid="passwordValidationList_container"
      >
        <h3
          className={styles.heading}
          data-testid="passwordValidationList_heading"
        >
          Loading password rules...
        </h3>
      </div>
    );
  }

  const renderRulesList = () => {
    const {
      passwordMinimumLength,
      passwordMaximumLength,
      ...booleanRules
    } = passwordPolicyRules;

    const rulesListItemCollection = Object.keys(booleanRules).map((rule) => {
      return (
        <RuleListItem
          isValidating={passwordValidationLoading}
          key={rule}
          ruleKey={rule}
          text={ruleListTextStrings[rule]}
          validationPassed={!!passwordValidationStatus?.[`${rule}Passed`]}
        />
      );
    });

    // Create a single list item from Min and Max Length rules,
    // add it to the front of the array (because why not?)
    if (!!passwordMinimumLength && !!passwordMaximumLength) {
      rulesListItemCollection.unshift(
        <RuleListItem
          isValidating={passwordValidationLoading}
          key={'passwordMinimumMaximumLength'}
          ruleKey={'passwordMinimumMaximumLength'}
          text={ruleListTextStrings.passwordMinimumMaximumLength(
            passwordMinimumLength,
            passwordMaximumLength,
          )}
          validationPassed={
            !!passwordValidationStatus?.passwordMinimumLengthPassed &&
            !!passwordValidationStatus?.passwordMaximumLengthPassed
          }
        />,
      );
    }

    return rulesListItemCollection;
  };

  return (
    <div
      className={classnames({
        [styles.inlineWrapper]: displayInline,
        [styles.wrapper]: !displayInline,
      })}
      data-testid="passwordValidationList_container"
    >
      <h3
        className={styles.heading}
        data-testid="passwordValidationList_heading"
      >
        Your password must:
      </h3>
      <ul data-testid="passwordValidationList_listWrapper">
        {renderRulesList()}
      </ul>
    </div>
  );
}

PasswordRulesValidationList.propTypes = {
  displayInline: PropTypes.bool,
  passwordPolicyRules: PropTypes.shape({
    dictionaryMatching: PropTypes.bool.isRequired,
    noLeadingOrTrailingSpaces: PropTypes.bool.isRequired,
    passwordMaximumLength: PropTypes.number.isRequired,
    passwordMinimumLength: PropTypes.number.isRequired,
    requiresLowercaseLetter: PropTypes.bool.isRequired,
    requiresSpecialCharacter: PropTypes.bool.isRequired,
    requiresUppercaseLetter: PropTypes.bool.isRequired,
  }),
  passwordValidationStatus: PropTypes.shape({
    dictionaryMatchingPassed: PropTypes.bool.isRequired,
    noLeadingOrTrailingSpacesPassed: PropTypes.bool.isRequired,
    passwordMaximumLengthPassed: PropTypes.bool.isRequired,
    passwordMinimumLengthPassed: PropTypes.bool.isRequired,
    requiresLowercaseLetterPassed: PropTypes.bool.isRequired,
    requiresSpecialCharacterPassed: PropTypes.bool.isRequired,
    requiresUppercaseLetterPassed: PropTypes.bool.isRequired,
  }),
  passwordPolicyRulesLoading: PropTypes.bool.isRequired,
  passwordValidationLoading: PropTypes.bool.isRequired,
};

PasswordRulesValidationList.defaultProps = {
  displayInline: false,
  passwordPolicyRules: null,
  passwordValidationStatus: null,
};
